/*
  Pack picker for the collection grid product cards.

  When a card's "add to cart" button is pressed, the quantity breaks already
  configured in Kaching for that product are read from its product page and
  offered in a dialog. Nothing is configured here: the deal bars, their copy
  and their colours all come from the app, and the final price is always the
  one Shopify applies through the automatic quantity discount.

  Products without a deal add straight to the cart, exactly as before.
*/
{
  const GRID = '#main-collection-product-grid';
  const CACHE_PREFIX = 'sa-pack-picker:';
  const CACHE_TTL = 30 * 60 * 1000;

  const requests = new Map();

  /* ---------------------------------------------------------------- money */

  const moneyFormat = (window.KROWN && KROWN.settings && KROWN.settings.shop_money_format) || '{{amount}}';

  const withDelimiters = (value, precision, thousands, decimal) => {
    const parts = (value / 100).toFixed(precision).split('.');
    return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (parts[1] ? decimal + parts[1] : '');
  };

  const formatMoney = (cents) => moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
    switch (name) {
      case 'amount': return withDelimiters(cents, 2, ',', '.');
      case 'amount_no_decimals': return withDelimiters(cents, 0, ',', '.');
      case 'amount_with_comma_separator': return withDelimiters(cents, 2, '.', ',');
      case 'amount_with_comma_separator_no_decimals': return withDelimiters(cents, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return withDelimiters(cents, 2, "'", '.');
      case 'amount_no_decimals_with_comma_separator': return withDelimiters(cents, 0, '.', ',');
      case 'amount_no_decimals_with_space_separator': return withDelimiters(cents, 0, ' ', ',');
      case 'amount_with_space_separator': return withDelimiters(cents, 2, ' ', ',');
      default: return withDelimiters(cents, 2, ',', '.');
    }
  });

  /* ------------------------------------------------------- reading the app */

  const jsonFromScript = (html, attribute) => {
    const marker = html.indexOf(attribute);
    if (marker < 0) return null;
    const open = html.indexOf('>', marker);
    const close = html.indexOf('</script>', open);
    if (open < 0 || close < 0) return null;
    try {
      return JSON.parse(html.slice(open + 1, close));
    } catch (error) {
      return null;
    }
  };

  const readCache = (url) => {
    try {
      const raw = sessionStorage.getItem(CACHE_PREFIX + url);
      if (!raw) return null;
      const entry = JSON.parse(raw);
      if (!entry || Date.now() - entry.at > CACHE_TTL) return null;
      return entry.data;
    } catch (error) {
      return null;
    }
  };

  const writeCache = (url, data) => {
    try {
      sessionStorage.setItem(CACHE_PREFIX + url, JSON.stringify({ at: Date.now(), data: data }));
    } catch (error) {
      /* private browsing or full quota: the in-memory cache still applies */
    }
  };

  const fetchDeal = (url) => {
    if (requests.has(url)) return requests.get(url);

    const cached = readCache(url);
    if (cached) {
      const resolved = Promise.resolve(cached);
      requests.set(url, resolved);
      return resolved;
    }

    const request = fetch(url, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        const data = {
          settings: jsonFromScript(html, 'class="kaching-bundles-deal-block-settings"'),
          translations: jsonFromScript(html, 'id="kaching-bundles-translations"')
        };
        writeCache(url, data);
        return data;
      })
      .catch(() => ({ settings: null, translations: null }));

    requests.set(url, request);
    return request;
  };

  /* --------------------------------------------------------- deal -> tiers */

  const translator = (translations) => {
    const locale = (window.Shopify && Shopify.locale) || document.documentElement.lang;
    const map = {};
    if (Array.isArray(translations) && locale) {
      translations.forEach((entry) => {
        if (!entry || entry.locale !== locale) return;
        const blocks = (entry.translations && entry.translations.dealBlocks) || {};
        Object.keys(blocks).forEach((id) => Object.assign(map, blocks[id]));
      });
    }
    return (text) => (text && map[text]) || text || '';
  };

  const fillTokens = (text, tier, bar) => text.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
    switch (name) {
      case 'saved_percentage': return bar.discountType === 'percentage' ? parseFloat(bar.discountValue) + '%' : '';
      case 'saved_amount': return formatMoney(tier.compare - tier.total);
      case 'total_price': return formatMoney(tier.total);
      case 'price_per_item': return formatMoney(Math.round(tier.total / tier.quantity));
      case 'quantity': return String(tier.quantity);
      default: return '';
    }
  }).replace(/\s{2,}/g, ' ').trim();

  /*
    Only plain quantity breaks are mirrored here. Anything this cannot price
    exactly on its own -- gifts, upsells, unknown discount types -- returns
    null so the card falls back to a normal add to cart and never shows a
    price that the checkout would not honour.
  */
  const buildTiers = (settings, translations, variant) => {
    const bars = settings && Array.isArray(settings.dealBars) ? settings.dealBars : [];
    if (bars.length < 2) return null;

    const unit = variant.price;
    if (!(unit > 0)) return null;

    const compareUnit = settings.useProductCompareAtPrice && variant.compare_at_price > unit
      ? variant.compare_at_price
      : unit;

    const translate = translator(translations);
    const tiers = [];

    for (let index = 0; index < bars.length; index += 1) {
      const bar = bars[index];
      if (!bar || bar.dealBarType !== 'quantity-break') return null;
      if ((bar.freeGifts && bar.freeGifts.length) || (bar.upsells && bar.upsells.length)) return null;

      const quantity = parseInt(bar.quantity, 10);
      if (!(quantity > 0)) return null;

      let unitPrice;
      const type = bar.discountType;
      if (!type || type === 'default' || type === 'none') {
        unitPrice = unit;
      } else if (type === 'percentage') {
        const percentage = parseFloat(bar.discountValue);
        if (!(percentage >= 0) || percentage >= 100) return null;
        /* Shopify truncates the per unit discount to whole cents, so 5% off
           5,95 EUR takes 0,29 and not 0,2975: rounding the price instead would
           quote three cents under what the cart charges. */
        unitPrice = unit - Math.floor((unit * percentage) / 100);
      } else {
        return null;
      }

      const tier = {
        quantity: quantity,
        total: unitPrice * quantity,
        compare: compareUnit * quantity,
        badge: translate(bar.badgeText),
        label: translate(bar.label)
      };
      tier.title = fillTokens(translate(bar.title), tier, bar);
      tier.subtitle = fillTokens(translate(bar.subtitle), tier, bar);
      tiers.push(tier);
    }

    return {
      heading: translate(settings.blockTitle),
      preselected: settings.preselectedDealBarId
        ? bars.findIndex((bar) => bar.id === settings.preselectedDealBarId)
        : -1,
      colors: settings.colors || {},
      radius: settings.cornerRadius,
      spacing: settings.spacing,
      tiers: tiers
    };
  };

  /* ------------------------------------------------------------ the dialog */

  const rgba = (color) => {
    if (!color) return '';
    const alpha = typeof color.alpha === 'number' ? color.alpha : 1;
    return 'rgba(' + color.red + ',' + color.green + ',' + color.blue + ',' + alpha + ')';
  };

  class SaPackPicker extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      if (this.parentElement !== document.body) document.body.appendChild(this);

      this.panel = this.querySelector('.sa-pack-picker__panel');
      this.headingElement = this.querySelector('.sa-pack-picker__heading');
      this.productElement = this.querySelector('.sa-pack-picker__product');
      this.barsElement = this.querySelector('.sa-pack-picker__bars');
      this.submitElement = this.querySelector('.sa-pack-picker__submit');

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-sa-close]')) this.close();
      });
      this.submitElement.addEventListener('click', () => this.confirm());
      this.onKeydown = (event) => {
        if (event.key === 'Escape') this.close();
      };
    }

    open(context, deal) {
      this.context = context;
      this.deal = deal;

      this.headingElement.textContent = deal.heading || '';
      this.headingElement.hidden = !deal.heading;
      this.productElement.textContent = context.title || '';
      this.productElement.hidden = !context.title;

      const colors = deal.colors;
      const style = this.panel.style;
      const tokens = {
        '--sa-pp-border': rgba(colors.border),
        '--sa-pp-bg': rgba(colors.background),
        '--sa-pp-selected-bg': rgba(colors.selectedBackground),
        '--sa-pp-heading': rgba(colors.blockTitle),
        '--sa-pp-title': rgba(colors.title),
        '--sa-pp-subtitle': rgba(colors.subtitle),
        '--sa-pp-price': rgba(colors.price),
        '--sa-pp-full-price': rgba(colors.fullPrice),
        '--sa-pp-badge-bg': rgba(colors.badgeBackground),
        '--sa-pp-badge-text': rgba(colors.badgeText),
        '--sa-pp-label-bg': rgba(colors.labelBackground),
        '--sa-pp-label-text': rgba(colors.label),
        '--sa-pp-cta-bg': rgba(colors.labelBackground) || rgba(colors.badgeBackground),
        '--sa-pp-cta-text': rgba(colors.label) || rgba(colors.badgeText)
      };
      Object.keys(tokens).forEach((name) => {
        if (tokens[name]) style.setProperty(name, tokens[name]);
      });
      if (deal.radius >= 0) style.setProperty('--sa-pp-radius', deal.radius + 'px');
      if (deal.spacing >= 0) style.setProperty('--sa-pp-gap', deal.spacing + 'rem');

      this.barsElement.textContent = '';
      deal.tiers.forEach((tier, index) => {
        this.barsElement.appendChild(this.buildBar(tier, index));
      });

      this.select(deal.preselected >= 0 ? deal.preselected : 0);

      this.hidden = false;
      document.documentElement.classList.add('sa-pack-picker-open');
      document.addEventListener('keydown', this.onKeydown);
      requestAnimationFrame(() => {
        this.classList.add('sa-pack-picker--visible');
        const selected = this.barsElement.querySelector('[aria-checked="true"]');
        if (selected) selected.focus({ preventScroll: true });
      });
    }

    buildBar(tier, index) {
      const bar = document.createElement('button');
      bar.type = 'button';
      bar.className = 'sa-pack-picker__bar';
      bar.setAttribute('role', 'radio');
      bar.setAttribute('aria-checked', 'false');
      bar.dataset.index = String(index);

      const mark = document.createElement('span');
      mark.className = 'sa-pack-picker__mark';
      mark.setAttribute('aria-hidden', 'true');
      bar.appendChild(mark);

      const text = document.createElement('span');
      text.className = 'sa-pack-picker__text';
      const title = document.createElement('span');
      title.className = 'sa-pack-picker__title';
      title.textContent = tier.title;
      text.appendChild(title);
      if (tier.subtitle) {
        const subtitle = document.createElement('span');
        subtitle.className = 'sa-pack-picker__subtitle';
        subtitle.textContent = tier.subtitle;
        text.appendChild(subtitle);
      }
      bar.appendChild(text);

      const prices = document.createElement('span');
      prices.className = 'sa-pack-picker__prices';
      const price = document.createElement('span');
      price.className = 'sa-pack-picker__price';
      price.textContent = formatMoney(tier.total);
      prices.appendChild(price);
      if (tier.compare > tier.total) {
        const compare = document.createElement('s');
        compare.className = 'sa-pack-picker__compare';
        compare.textContent = formatMoney(tier.compare);
        prices.appendChild(compare);
      }
      bar.appendChild(prices);

      if (tier.badge) {
        const badge = document.createElement('span');
        badge.className = 'sa-pack-picker__badge';
        badge.textContent = tier.badge;
        bar.appendChild(badge);
      }

      bar.addEventListener('click', () => this.select(index));
      return bar;
    }

    select(index) {
      this.selected = index;
      this.barsElement.querySelectorAll('.sa-pack-picker__bar').forEach((bar, position) => {
        bar.setAttribute('aria-checked', position === index ? 'true' : 'false');
        bar.classList.toggle('sa-pack-picker__bar--selected', position === index);
      });
    }

    close() {
      this.classList.remove('sa-pack-picker--visible');
      this.hidden = true;
      document.documentElement.classList.remove('sa-pack-picker-open');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.context && this.context.button) this.context.button.focus({ preventScroll: true });
    }

    confirm() {
      const tier = this.deal.tiers[this.selected];
      const form = this.context.form;
      if (!tier || !form) return this.close();
      this.close();
      submitWithQuantity(form, tier.quantity);
    }
  }

  if (!customElements.get('sa-pack-picker')) customElements.define('sa-pack-picker', SaPackPicker);

  /* ----------------------------------------------------------- the trigger */

  /*
    The quantity rides along in the same form the theme already submits, so the
    add goes through the theme's own cart handler and the cart drawer opens as
    it always does.
  */
  const submitWithQuantity = (form, quantity) => {
    let input = form.querySelector('input[name="quantity"]');
    const borrowed = !input;
    if (borrowed) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      form.appendChild(input);
    }
    input.value = String(quantity);

    if (form.requestSubmit) form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));

    if (borrowed) input.remove();
    else input.value = '1';
  };

  const contextFor = (button) => {
    const form = button.closest('form');
    const quickBuy = button.closest('quick-buy');
    if (!form || !quickBuy) return null;

    const variants = quickBuy.querySelector('product-variants');
    const variantData = quickBuy.querySelector('[data-js-variant-data]');
    const selectedId = form.querySelector('input[name="id"]');
    if (!variants || !variantData || !selectedId) return null;

    let variant;
    try {
      variant = JSON.parse(variantData.textContent);
    } catch (error) {
      return null;
    }
    /* a stale variant block would mean pricing the wrong thing */
    if (!variant || String(variant.id) !== String(selectedId.value)) return null;

    const card = button.closest('[data-js-product-item]');
    const titleElement = card && card.querySelector('.product-item__title');

    return {
      form: form,
      button: button,
      url: variants.dataset.url,
      variant: variant,
      title: titleElement ? titleElement.textContent.trim() : ''
    };
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('button[name="add"]');
    if (!button || button.disabled || button.classList.contains('disabled')) return;
    if (!button.closest(GRID)) return;

    const element = document.querySelector('sa-pack-picker');
    if (!element) return;

    const context = contextFor(button);
    if (!context || !context.url) return;

    event.preventDefault();
    event.stopPropagation();

    button.classList.add('working');
    fetchDeal(context.url).then((data) => {
      button.classList.remove('working');
      const deal = data && data.settings ? buildTiers(data.settings, data.translations, context.variant) : null;
      if (!deal) {
        /* no pack for this product: add the single unit as usual */
        submitWithQuantity(context.form, 1);
        return;
      }
      element.open(context, deal);
    });
  }, true);

  /* warm the cache before the click lands */
  const prefetch = (event) => {
    const card = event.target.closest && event.target.closest('[data-js-product-item]');
    if (!card || !card.closest(GRID)) return;
    const variants = card.querySelector('product-variants');
    if (variants && variants.dataset.url) fetchDeal(variants.dataset.url);
  };
  document.addEventListener('pointerenter', prefetch, true);
  document.addEventListener('touchstart', prefetch, { capture: true, passive: true });
}
